'use client'

import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { AnimatedNumber } from '@/components/ui/animated-number'
import {
  EASE,
  Eyebrow,
  HAIRLINE,
  QuietLink,
  useReveal,
} from '@/components/page-kit'
import { getStory, customerStories } from '@/config/customer-stories'

import { RecordEntry, RecordList } from './record-kit'

/* Every string on this page comes from @/config/customer-stories. The entry
   labels ("The challenge", "Where it stood", …) are structural labels that are
   identical on all seven records — the old template's headings made claims of
   their own ("Every transformation begins with a challenge", "Built for X, from
   the ground up"), which are assertions no story's data supports. */

/* The "Delivered" field lists what was built, in the story's own words. Where a
   line names something with a page of its own, it becomes the link to it —
   these seven records were the only pages linking into the catalogue, and they
   linked to nothing. Entries absent from this map ("Admin Panel", "Website
   Revamp", "AI Interview Portal", …) stay plain text: there is no page to
   point at, and inventing one would misdescribe what was delivered. */
const DELIVERED_HREF: Record<string, string | undefined> = {
  LMS: '/products/lms',
  'Finance Module': '/products/accounts',
  'Online Examination': '/schoolerp/features/examination-results',
  Cloud: '/solutions/cloud-infrastructure',
}

export function StoryPage({ slug }: { slug: string }) {
  const story = getStory(slug)
  if (!story) return null

  const stats = Object.values(story.stats ?? {})
  const related = customerStories
    .filter((s) => s.slug !== story.slug)
    .slice(0, 3)

  return (
    <main className="reveal-scope relative bg-background">
      <StoryHead story={story} stats={stats} />

      <RecordEntry entry={1} label="The challenge">
        <p className="max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {story.challenge}
        </p>
      </RecordEntry>

      <RecordEntry entry={2} label="Where it stood">
        {story.journeyDescription && (
          <p className="mb-8 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {story.journeyDescription}
          </p>
        )}
        <RecordList items={story.journey} tone="before" />
      </RecordEntry>

      <RecordEntry entry={3} label="What was built">
        <p className="max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {story.solution}
        </p>

        {story.whatWeBuiltDescription && (
          <p className="font-heading mt-8 max-w-3xl text-lg font-normal leading-relaxed text-foreground sm:text-xl">
            {story.whatWeBuiltDescription}
          </p>
        )}

        <div className={cn('mt-10 border-t', HAIRLINE)}>
          {story.whatWeBuilt.map((w, i) => (
            <BuiltRow key={w.title} item={w} index={i} />
          ))}
        </div>
      </RecordEntry>

      {story.showcase?.length > 0 && (
        <RecordEntry entry={4} label="In use">
          <div className="space-y-16 sm:space-y-20">
            {story.showcase.map((s, i) => (
              <ShowcaseBlock key={s.heading} item={s} index={i} />
            ))}
          </div>
        </RecordEntry>
      )}

      <RecordEntry entry={5} label="Where it stands">
        <RecordList items={story.milestones} tone="after" />
        {story.outcome && (
          <p className="font-heading mt-10 max-w-3xl text-lg font-normal leading-relaxed text-foreground sm:text-xl lg:text-2xl lg:leading-[1.45]">
            {story.outcome}
          </p>
        )}
      </RecordEntry>

      {story.testimonial?.quote && (
        <RecordEntry entry={6} label="In their words">
          <Testimonial story={story} />
        </RecordEntry>
      )}

      <StoryClose related={related} />
    </main>
  )
}

/* ─── Record head ──────────────────────────────────────────────────────── */
function StoryHead({
  story,
  stats,
}: {
  story: NonNullable<ReturnType<typeof getStory>>
  stats: {
    value: string
    label: string
    icon: React.ComponentType<{ className?: string }>
  }[]
}) {
  const band = useReveal<HTMLDivElement>(0.25)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[70svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          {/* The trail used to stop at the listing page, so it never named the
              record the reader was on and emitted no BreadcrumbList. */}
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Customer Stories', href: '/customer-stories' },
              {
                name: story.institutionName,
                href: `/customer-stories/${story.slug}`,
              },
            ]}
          />

          <div className="mt-6">
            <Eyebrow>Customer story</Eyebrow>
          </div>

          <h1 className="font-heading mt-6 max-w-4xl text-[30px]/[1.14] font-normal tracking-tight text-foreground sm:text-[40px]/[1.14] md:text-5xl/[1.12] lg:text-[56px]/[1.1]">
            <ChromaText>{story.institutionName}</ChromaText>
          </h1>

          <p className="mt-5 max-w-2xl text-lg font-medium leading-snug text-emerald-600 sm:text-xl md:text-2xl dark:text-primary-active">
            {story.institutionSubtitle}
          </p>

          {/* Record fields — no map pins, no tinted pills. */}
          <dl className="mt-8 flex flex-wrap gap-x-12 gap-y-4">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                Location
              </dt>
              <dd className="mt-1.5 text-sm text-foreground sm:text-base">
                {story.location}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                Segment
              </dt>
              <dd className="mt-1.5 text-sm text-foreground sm:text-base">
                {story.category}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Scope band: the institution's figures and what was delivered. Rule
          full-bleed, cells in the container so the first lines up with the
          headline above. */}
      <div ref={band.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="container mx-auto">
          <div className="grid grid-cols-2 gap-px bg-border/70 lg:grid-cols-4">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className="bg-background py-5 pl-4 pr-4 first:pl-0 sm:py-6 sm:pl-5 sm:pr-5"
                style={{
                  opacity: band.shown ? 1 : 0,
                  transition: `opacity 460ms ${EASE} ${i * 70}ms`,
                }}
              >
                <div className="font-heading text-2xl font-normal tabular-nums leading-none text-foreground sm:text-3xl">
                  <AnimatedNumber value={s.value} delay={i * 70} />
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {s.label}
                </div>
              </div>
            ))}

            <div
              className="bg-background py-5 pl-4 pr-4 sm:py-6 sm:pl-5 sm:pr-5"
              style={{
                opacity: band.shown ? 1 : 0,
                transition: `opacity 460ms ${EASE} ${stats.length * 70}ms`,
              }}
            >
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                Delivered
              </div>
              <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {story.technologies.map((t) => {
                  const href = DELIVERED_HREF[t]

                  return (
                    <li
                      key={t}
                      className="font-heading text-[13px] font-normal leading-tight text-foreground"
                    >
                      {href ? (
                        <Link
                          href={href}
                          className={cn(
                            'underline decoration-emerald-600/40 underline-offset-4 transition-colors',
                            'hover:decoration-emerald-600',
                            'dark:decoration-primary-active/40 dark:hover:decoration-primary-active',
                            'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
                            'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
                          )}
                        >
                          {t}
                        </Link>
                      ) : (
                        t
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Entry 03 rows ────────────────────────────────────────────────────── */
function BuiltRow({
  item,
  index,
}: {
  item: { title: string; description: string; tag: string }
  index: number
}) {
  const row = useReveal<HTMLDivElement>(0.15)

  return (
    <div
      ref={row.ref}
      className={cn(
        'grid grid-cols-1 gap-x-10 gap-y-2 border-b py-6 sm:py-7',
        'lg:grid-cols-[minmax(0,220px)_1fr] lg:items-baseline',
        HAIRLINE
      )}
      style={{
        opacity: row.shown ? 1 : 0,
        transform: row.shown ? 'none' : 'translateY(10px)',
        transition: `opacity 500ms ${EASE} ${index * 80}ms, transform 500ms ${EASE} ${index * 80}ms`,
      }}
    >
      <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        {item.tag}
      </span>

      <div className="min-w-0">
        {/* h2: the record's entry labels are set on the tab rule as spans, not
            headings, so these rows are the first heading level under the h1. */}
        <h2 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
          {item.title}
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          {item.description}
        </p>
      </div>
    </div>
  )
}

/* ─── Entry 04 showcase ────────────────────────────────────────────────── */
function ShowcaseBlock({
  item,
  index,
}: {
  item: {
    tag: string
    heading: string
    description: string
    highlight: string
    imageUrl: string
    imageAlt: string
  }
  index: number
}) {
  const block = useReveal<HTMLDivElement>(0.12)

  return (
    <article
      ref={block.ref}
      className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start lg:gap-14"
      style={{
        opacity: block.shown ? 1 : 0,
        transform: block.shown ? 'none' : 'translateY(12px)',
        transition: `opacity 600ms ${EASE}, transform 600ms ${EASE}`,
      }}
    >
      <div className={cn(index % 2 === 1 && 'lg:order-2')}>
        <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          {item.tag}
        </span>
        <h2 className="font-heading mt-4 text-xl font-normal leading-snug text-foreground sm:text-2xl lg:text-[28px] lg:leading-[1.25]">
          {item.heading}
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          {item.description}
        </p>
        <p
          className={cn(
            'mt-6 border-t pt-5 text-sm text-foreground sm:text-base',
            HAIRLINE
          )}
        >
          {item.highlight}
        </p>
      </div>

      <div
        className={cn(
          'relative aspect-[16/10] w-full overflow-hidden rounded-2xl border',
          HAIRLINE,
          index % 2 === 1 && 'lg:order-1'
        )}
      >
        <Image
          src={item.imageUrl}
          alt={item.imageAlt}
          fill
          sizes="(min-width:1024px) 50vw, 100vw"
          className="object-cover"
        />
      </div>
    </article>
  )
}

/* ─── Entry 06 testimonial ─────────────────────────────────────────────── */
function Testimonial({
  story,
}: {
  story: NonNullable<ReturnType<typeof getStory>>
}) {
  const block = useReveal<HTMLQuoteElement>(0.2)

  return (
    <blockquote
      ref={block.ref}
      className="max-w-4xl"
      style={{
        opacity: block.shown ? 1 : 0,
        transform: block.shown ? 'none' : 'translateY(12px)',
        transition: `opacity 700ms ${EASE}, transform 700ms ${EASE}`,
      }}
    >
      <p className="font-heading text-2xl font-normal leading-[1.3] tracking-tight text-foreground sm:text-3xl lg:text-[36px] lg:leading-[1.28]">
        &ldquo;{story.testimonial.quote}&rdquo;
      </p>

      <footer className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="relative h-9 w-28 shrink-0 sm:h-10 sm:w-36">
          <Image
            src={story.logo}
            alt={story.institution}
            fill
            sizes="144px"
            className={cn(
              'object-contain object-left',
              'opacity-90 mix-blend-multiply grayscale',
              'dark:opacity-100 dark:mix-blend-screen dark:grayscale dark:invert'
            )}
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <cite className="text-sm font-medium not-italic text-foreground">
            {story.testimonial.name}
          </cite>
          <span className="text-sm text-muted-foreground">
            {story.testimonial.role}
          </span>
        </div>
      </footer>
    </blockquote>
  )
}

/* ─── Close ────────────────────────────────────────────────────────────── */
function StoryClose({
  related,
}: {
  related: {
    slug: string
    institutionName: string
    institutionSubtitle: string
  }[]
}) {
  const list = useReveal<HTMLDivElement>(0.15)

  return (
    <section className="relative mt-16 overflow-hidden sm:mt-20 md:mt-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[80%]"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div className="container relative z-10 mx-auto py-16 sm:py-20">
        <Eyebrow>More records</Eyebrow>

        <div ref={list.ref} className={cn('mt-8 border-t', HAIRLINE)}>
          {related.map((r, i) => (
            <Link
              key={r.slug}
              href={`/customer-stories/${r.slug}`}
              className={cn(
                'group grid grid-cols-1 gap-x-10 gap-y-1 border-b py-5 sm:py-6',
                'lg:grid-cols-[1fr_auto] lg:items-baseline',
                'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4',
                'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(8px)',
                transition: `opacity 460ms ${EASE} ${i * 80}ms, transform 460ms ${EASE} ${i * 80}ms`,
              }}
            >
              <span className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                {r.institutionName}
              </span>
              <span className="text-[13px] text-muted-foreground lg:text-right">
                {r.institutionSubtitle}
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <QuietLink href="/customer-stories">All customer stories</QuietLink>

          <Button asChild size="xl">
            <a href="/schedule-meeting">Talk to us</a>
          </Button>
        </div>
      </div>
    </section>
  )
}
